import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Row
} from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { FormInstance } from 'antd/lib'
import { useState } from 'react'

import {
  AuscultatoryCardiacExamStatus,
  ChestForm,
  Conscience,
  GeneralState,
  PeripheralLymphNodesStatus,
  Position,
  Rales,
  VesicularSounds
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

import { ConsultationFormAbdominalSection } from './ConsultationFormAbdominalSection'
import { ConsultationFormGenitalSection } from './ConsultationFormGenitalSection'
import { ConsultationFormNeurologicalSection } from './ConsultationFormNeurologicalSection'
import { ConsultationFormUrinarySection } from './ConsultationFormUrinarySection'

type Props = {
  form: FormInstance
  isPrimary: boolean
}

export const ConsultationFormPhysicalExaminationSection = ({
  form,
  isPrimary
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [isOtherCutaneous, setIsOtherCutaneous] = useState(false)
  const [isOtherOropharynx, setIsOtherOropharynx] = useState(false)
  const [isCardiacArrhythmia, setIsCardiacArrhythmia] = useState(false)
  const [isNodesEnlarged, setIsNodesEnlarged] = useState(false)

  const onCutaneousTegumentsChange = (e: CheckboxChangeEvent) => {
    const value = e.target.checked

    setIsOtherCutaneous(value)

    form.setFieldsValue({
      form: {
        physicalExaminationSection: {
          cutaneousTeguments: {
            otherModifications: {
              isSelected: value,
              value: undefined
            }
          }
        }
      }
    })
  }

  const onOropharynxChange = (e: CheckboxChangeEvent) => {
    const value = e.target.checked

    setIsOtherOropharynx(value)

    form.setFieldsValue({
      form: {
        physicalExaminationSection: {
          oropharynx: {
            modification: {
              isSelected: value,
              value: undefined
            }
          }
        }
      }
    })
  }

  const onArrhythmiaChange = (e: RadioChangeEvent) => {
    const value = e.target.value

    setIsCardiacArrhythmia(value === AuscultatoryCardiacExamStatus.ARRHYTHMIA)

    form.setFieldsValue({
      form: {
        physicalExaminationSection: {
          cardiacExam: {
            auscultatoryCardiacExam: {
              value: value,
              comments: undefined
            }
          }
        }
      }
    })
  }

  const onNodesChange = (e: RadioChangeEvent) => {
    const value = e.target.value

    setIsNodesEnlarged(value === PeripheralLymphNodesStatus.ENLARGED)

    form.setFieldsValue({
      form: {
        physicalExaminationSection: {
          peripheralLymphNodes: {
            value: value,
            comments: undefined
          }
        }
      }
    })
  }

  return (
    <>
      <Col span={24}>
        <Text size="xl">
          {t('consultation_form.physical_examination_section')}
        </Text>
      </Col>

      <Col span={24}>
        <Form.Item
          name={['form', 'physicalExaminationSection', 'accusations']}
          label={`${isPrimary ? '14.' : '9.'} ${t('consultation_form.accusations')}`}
        >
          <Input.TextArea placeholder={t('consultation_form.to_specify')} />
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={['form', 'physicalExaminationSection', 'generalState']}
          label={`${isPrimary ? '15.' : '10.'}  ${t('consultation_form.general_state.title')}`}
          rules={requiredRules}
        >
          <Radio.Group>
            {Object.values(GeneralState).map((item) => (
              <Radio key={item} value={item}>
                {t(`consultation_form.general_state.${item}`)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={['form', 'physicalExaminationSection', 'conscience']}
          label={`${isPrimary ? '16.' : '11.'} ${t('consultation_form.conscience.title')}`}
        >
          <Radio.Group>
            {Object.values(Conscience)
              .filter((item) =>
                isPrimary
                  ? true
                  : ![Conscience.CONFUSED, Conscience.DELIRIUM].includes(item)
              )
              .map((item) => (
                <Radio key={item} value={item}>
                  {t(`consultation_form.conscience.${item}`)}
                </Radio>
              ))}
          </Radio.Group>
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={['form', 'physicalExaminationSection', 'position']}
          label={`${isPrimary ? '17.' : '12.'}  ${t('consultation_form.position.title')}`}
        >
          <Radio.Group>
            {Object.values(Position).map((item) => (
              <Radio key={item} value={item}>
                {t(`consultation_form.position.${item}`)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Text>
          {isPrimary ? '18.' : '13.'}{' '}
          {t('consultation_form.constitutional_type.title')}
        </Text>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'constitutionalType',
                'astenic'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.constitutional_type.astenic')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'constitutionalType',
                'normostenic'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.constitutional_type.normostenic')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'constitutionalType',
                'hyperstenic'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.constitutional_type.hyperstenic')}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Text>
          {isPrimary ? '19.' : '14.'}{' '}
          {t('consultation_form.vital_physiological_indices')}
        </Text>

        <Row gutter={[12, 12]} className="mt-16">
          <Col span={12}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'vitalPhysiologicalIndices',
                'pulse'
              ]}
              label={t('consultation_form.pulse')}
            >
              <Input placeholder={t('consultation_form.pulse')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'vitalPhysiologicalIndices',
                'temperature'
              ]}
              label={t('consultation_form.temperature')}
            >
              <Input placeholder={t('consultation_form.temperature')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'vitalPhysiologicalIndices',
                'hypertension'
              ]}
              label={t('consultation_form.hypertension')}
            >
              <Input placeholder={t('consultation_form.hypertension')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'vitalPhysiologicalIndices',
                'breathingFrequency'
              ]}
              label={t('consultation_form.breathing_frequency')}
            >
              <Input placeholder={t('consultation_form.breathing_frequency')} />
            </Form.Item>
          </Col>
        </Row>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Form.Item
          name={[
            'form',
            'physicalExaminationSection',
            'peripheralLymphNodes',
            'value'
          ]}
          rules={requiredRules}
          label={`${isPrimary ? '20.' : '15.'} ${t('consultation_form.peripheral_lymph_nodes.title')}`}
        >
          <Radio.Group onChange={onNodesChange}>
            <Radio value={PeripheralLymphNodesStatus.ENLARGED}>
              {t(`consultation_form.peripheral_lymph_nodes.enlarged`)}
            </Radio>

            <Radio value={PeripheralLymphNodesStatus.NOT_ENLARGED}>
              {t(`consultation_form.peripheral_lymph_nodes.notEnlarged`)}
            </Radio>
          </Radio.Group>
        </Form.Item>

        {isNodesEnlarged && (
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'peripheralLymphNodes',
              'comments'
            ]}
            rules={requiredRules}
          >
            <Input.TextArea placeholder={t('consultation_form.to_specify')} />
          </Form.Item>
        )}
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Text>
          {isPrimary ? '21.' : '16.'}{' '}
          {t('consultation_form.cutaneous_teguments.title')}
        </Text>

        <Row gutter={[12, 12]} className="mt-16">
          <Col span={6}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'cutaneousTeguments',
                'activeSignsOfShingles'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t(
                  'consultation_form.cutaneous_teguments.activeSignsOfShingles'
                )}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'cutaneousTeguments',
                'vascularStars'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.cutaneous_teguments.vascularStars')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'cutaneousTeguments',
                'placesToInjectDrugs'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.cutaneous_teguments.placesToInjectDrugs')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'cutaneousTeguments',
                'postSignsOfShingles'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.cutaneous_teguments.postSignsOfShingles')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'cutaneousTeguments',
                'seborrheicDermatitis'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t(
                  'consultation_form.cutaneous_teguments.seborrheicDermatitis'
                )}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'cutaneousTeguments',
                'otherModifications',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox onChange={onCutaneousTegumentsChange}>
                {t('consultation_form.cutaneous_teguments.otherModifications')}
              </Checkbox>
            </Form.Item>
          </Col>

          {isOtherCutaneous && (
            <Col span={12}>
              <Form.Item
                name={[
                  'form',
                  'physicalExaminationSection',
                  'cutaneousTeguments',
                  'otherModifications',
                  'value'
                ]}
              >
                <Input.TextArea
                  placeholder={t('consultation_form.to_specify')}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Text>
          {isPrimary ? '22.' : '17.'} {t('consultation_form.oropharynx.title')}
        </Text>

        <Row gutter={[12, 12]} className="mt-16">
          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'oropharynx',
                'noModification'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.oropharynx.noModification')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'oropharynx',
                'villousLeukoplakiaOfTheOralCavity'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t(
                  'consultation_form.oropharynx.villousLeukoplakiaOfTheOralCavity'
                )}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'oropharynx',
                'candidalStomatitis'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.oropharynx.candidalStomatitis')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'oropharynx',
                'primarySyphilis'
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                {t('consultation_form.oropharynx.primarySyphilis')}
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'oropharynx',
                'modification',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox onChange={onOropharynxChange}>
                {t('consultation_form.oropharynx.modification')}
              </Checkbox>
            </Form.Item>
          </Col>

          {isOtherOropharynx && (
            <Form.Item
              name={[
                'form',
                'physicalExaminationSection',
                'oropharynx',
                'modification',
                'value'
              ]}
            >
              <Input.TextArea placeholder={t('consultation_form.to_specify')} />
            </Form.Item>
          )}
        </Row>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Text>
          {isPrimary ? '23.' : '18.'}{' '}
          {t('consultation_form.chest_and_lungs.title')}
        </Text>

        <Form.Item
          className="ml-16 mt-16"
          name={[
            'form',
            'physicalExaminationSection',
            'chestAndLungs',
            'chestForm'
          ]}
          label={`${isPrimary ? '23.1' : '18.1'} ${t('consultation_form.chest_and_lungs.chest_form')}`}
          layout="horizontal"
        >
          <Radio.Group>
            {Object.values(ChestForm).map((value) => (
              <Radio value={value} key={value}>
                {t(`consultation_form.chest_and_lungs.${value}`)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        <div className="ml-16 mt-16">
          <Text>
            {isPrimary ? '23.2' : '18.2'}{' '}
            {t('consultation_form.chest_and_lungs.auscultatory')}
          </Text>

          <Form.Item
            className="ml-16 mt-16"
            name={[
              'form',
              'physicalExaminationSection',
              'chestAndLungs',
              'auscultatory',
              'rales'
            ]}
            label={`${isPrimary ? '23.2.1' : '18.2.1'} ${t('consultation_form.chest_and_lungs.rales')}`}
            layout="horizontal"
          >
            <Radio.Group>
              {Object.values(Rales).map((value) => (
                <Radio value={value} key={value}>
                  {t(`consultation_form.chest_and_lungs.${value}`)}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="ml-16"
            name={[
              'form',
              'physicalExaminationSection',
              'chestAndLungs',
              'auscultatory',
              'vesicularSounds'
            ]}
            label={`${isPrimary ? '23.2.2' : '18.2.2'} ${t('consultation_form.chest_and_lungs.vesicular_sounds')}`}
            layout="horizontal"
          >
            <Radio.Group>
              {Object.values(VesicularSounds).map((value) => (
                <Radio value={value} key={value}>
                  {t(`consultation_form.chest_and_lungs.${value}`)}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </div>
      </Col>

      <Divider className={styles.divider} />

      <Col span={24}>
        <Text>
          {isPrimary ? '24.' : '19.'}{' '}
          {t('consultation_form.cardiac_exam.title')}
        </Text>

        <Form.Item
          name={[
            'form',
            'physicalExaminationSection',
            'cardiacExam',
            'auscultatoryCardiacExam',
            'value'
          ]}
          className="mt-16 ml-16"
          label={`${isPrimary ? '24.1' : '19.1'} ${t('consultation_form.cardiac_exam.auscultatory_cardiac_exam')}`}
        >
          <Radio.Group onChange={onArrhythmiaChange}>
            {Object.values(AuscultatoryCardiacExamStatus)
              .filter(
                (item) => item !== AuscultatoryCardiacExamStatus.ARRHYTHMIA
              )
              .map((value) => (
                <Radio value={value} key={value}>
                  {t(`consultation_form.cardiac_exam.${value}`)}
                </Radio>
              ))}

            <Radio value={AuscultatoryCardiacExamStatus.ARRHYTHMIA}>
              {t(`consultation_form.cardiac_exam.arrhythmia`)}
            </Radio>
          </Radio.Group>
        </Form.Item>

        {isCardiacArrhythmia && (
          <Form.Item
            name={[
              'form',
              'physicalExaminationSection',
              'cardiacExam',
              'auscultatoryCardiacExam',
              'comments'
            ]}
            rules={requiredRules}
          >
            <Input.TextArea placeholder={t('consultation_form.to_specify')} />
          </Form.Item>
        )}
      </Col>

      <Divider className={styles.divider} />

      <ConsultationFormAbdominalSection isPrimary={isPrimary} />

      <Divider className={styles.divider} />

      <ConsultationFormGenitalSection isPrimary={isPrimary} form={form} />

      <Divider className={styles.divider} />

      <ConsultationFormNeurologicalSection isPrimary={isPrimary} form={form} />

      <Divider className={styles.divider} />

      <ConsultationFormUrinarySection isPrimary={isPrimary} form={form} />
    </>
  )
}
